/**
 * Metastable consensus (Snowball / Avalanche) over the DAG.
 *
 * Why this rather than proof of work or a staked committee. PoW buys
 * security with electricity and gives finality in minutes; a staked
 * committee gives fast finality but hands the network to whoever holds the
 * most money, which is precisely the arrangement FACE exists to route
 * around. Repeated random subsampling gives sub-second finality, costs
 * almost nothing to participate in, has no committee to subpoena or
 * sanction, and - the property that matters here - its failure
 * probability falls off *exponentially* in the sample size k. So:
 *
 *     more nodes -> larger k (see adaptive() in params.ts)
 *                -> exponentially smaller chance an adversary flips a
 *                   decision.
 *
 * The network is literally harder to attack the more people are using it,
 * and because sampling is O(k) per node regardless of network size, it is
 * also no slower.
 *
 * The loop, per round: ask k random peers whether they currently prefer a
 * vertex. If at least alpha of them say yes, the vertex earns a chit, and
 * that chit's confidence flows back through everything it references. A
 * vertex whose conflict set it leads, whose whole causal past is accepted,
 * and which has held its lead for beta consecutive rounds, is final.
 */

import { adaptive, type AdaptiveParams } from './params.ts';
import type { Dag } from './dag.ts';
import { conflictKey, type SignedVertex, type VertexId } from './tx.ts';

export interface ConflictSetState {
  key: string;
  members: Set<VertexId>;
  /** Current leader by confidence. */
  preference: VertexId;
  /** Leader as of the previous successful round, for the streak counter. */
  lastPreference: VertexId;
  consecutiveSuccesses: number;
  decided?: VertexId;
}

export interface VoteRecord {
  chits: number;
  /** Own chits plus every descendant's - the vertex's accumulated support. */
  confidence: number;
  queried: boolean;
}

export interface PollOutcome {
  id: VertexId;
  yes: number;
  asked: number;
}

export class Consensus {
  private sets = new Map<string, ConflictSetState>();
  private setOf = new Map<VertexId, string>();
  private votes = new Map<VertexId, VoteRecord>();
  private accepted = new Set<VertexId>();
  private rejected = new Set<VertexId>();
  private dag: Dag;
  private validity: (v: SignedVertex) => boolean;
  params: AdaptiveParams;

  constructor(dag: Dag, validity: (v: SignedVertex) => boolean, nodeCount = 1) {
    this.dag = dag;
    this.validity = validity;
    this.params = adaptive(nodeCount);
  }

  setNodeCount(n: number): void {
    this.params = adaptive(n);
  }

  /** Rogue conflict sets need more evidence than uncontested ones. */
  private get betaVirtuous(): number {
    return this.params.beta;
  }
  private get betaRogue(): number {
    return this.params.beta * 3;
  }

  track(v: SignedVertex): void {
    if (this.votes.has(v.id)) return;
    this.votes.set(v.id, { chits: 0, confidence: 0, queried: false });

    const key = conflictKey(v);
    this.setOf.set(v.id, key);
    let set = this.sets.get(key);
    if (!set) {
      set = { key, members: new Set(), preference: v.id, lastPreference: v.id, consecutiveSuccesses: 0 };
      this.sets.set(key, set);
    }
    set.members.add(v.id);
    // A newcomer to a contested set never displaces the incumbent leader:
    // first-seen wins ties, which is what makes double-spends lose.
    if (set.members.size > 1) set.consecutiveSuccesses = 0;
  }

  vote(id: VertexId): VoteRecord {
    let r = this.votes.get(id);
    if (!r) this.votes.set(id, (r = { chits: 0, confidence: 0, queried: false }));
    return r;
  }

  conflictState(id: VertexId): ConflictSetState | undefined {
    const key = this.setOf.get(id);
    return key ? this.sets.get(key) : undefined;
  }

  isAccepted(id: VertexId): boolean {
    return this.accepted.has(id) || this.dag.isAnchor(id);
  }
  isRejected(id: VertexId): boolean {
    return this.rejected.has(id);
  }
  isDecided(id: VertexId): boolean {
    return this.isAccepted(id) || this.isRejected(id);
  }

  /** Leader of its own conflict set. */
  isPreferred(id: VertexId): boolean {
    if (this.dag.isAnchor(id)) return true;
    if (this.rejected.has(id)) return false;
    const set = this.conflictState(id);
    if (!set) return false;
    if (set.decided) return set.decided === id;
    return set.preference === id;
  }

  /**
   * Preferred, and so is every vertex it rests on. This is the predicate a
   * node answers queries with, and it is what makes a vote on one vertex
   * an implicit vote on its entire causal history.
   */
  isStronglyPreferred(id: VertexId, depthCap = 2048): boolean {
    if (this.dag.isAnchor(id)) return true;
    const entry = this.dag.get(id);
    if (!entry) return false;
    if (!this.isPreferred(id)) return false;
    if (!this.validity(entry.vertex)) return false;

    const seen = new Set<VertexId>([id]);
    const queue = [...entry.vertex.parents];
    let visited = 0;
    while (queue.length && visited < depthCap) {
      const cur = queue.shift()!;
      if (seen.has(cur)) continue;
      seen.add(cur);
      visited++;
      if (this.dag.isAnchor(cur)) continue;
      if (this.accepted.has(cur)) continue; // accepted past needs no re-check
      if (this.rejected.has(cur)) return false;
      if (!this.isPreferred(cur)) return false;
      const e = this.dag.get(cur);
      if (!e) return false;
      for (const p of e.vertex.parents) if (!seen.has(p)) queue.push(p);
    }
    return true;
  }

  /** The reply this node gives when a peer polls it about these vertices. */
  answerQuery(ids: VertexId[]): Record<VertexId, boolean> {
    const out: Record<VertexId, boolean> = {};
    for (const id of ids) out[id] = this.isStronglyPreferred(id);
    return out;
  }

  /**
   * Fold one round of k peer answers into the state machine.
   * Returns the ids that became final in this round.
   */
  recordPoll(outcomes: PollOutcome[]): { accepted: VertexId[]; rejected: VertexId[] } {
    const newlyAccepted: VertexId[] = [];
    const newlyRejected: VertexId[] = [];

    for (const o of outcomes) {
      if (this.isDecided(o.id)) continue;
      const rec = this.vote(o.id);
      rec.queried = true;
      const set = this.conflictState(o.id);
      if (!set) continue;

      if (o.asked > 0 && o.yes >= this.params.alpha) {
        rec.chits = 1;
        this.propagateConfidence(o.id);

        // Leadership within the conflict set follows confidence.
        const leader = this.leaderOf(set);
        set.preference = leader;

        if (set.preference === set.lastPreference) {
          set.consecutiveSuccesses++;
        } else {
          set.lastPreference = set.preference;
          set.consecutiveSuccesses = 1;
        }
      } else {
        set.consecutiveSuccesses = 0;
      }
    }

    // Finalisation is a separate sweep: a vertex can only be accepted once
    // its whole past is accepted, and accepting one vertex may unblock
    // several children in the same pass.
    for (let pass = 0; pass < 4; pass++) {
      let progressed = false;
      for (const set of this.sets.values()) {
        if (set.decided) continue;
        const id = set.preference;
        if (this.isDecided(id)) continue;
        const rec = this.vote(id);
        const rogue = set.members.size > 1;
        const threshold = rogue ? this.betaRogue : this.betaVirtuous;
        const readyByStreak = set.consecutiveSuccesses >= threshold;
        const readyByWeight = rec.confidence >= threshold * 2;
        if (!(readyByStreak || readyByWeight)) continue;
        if (!this.pastAccepted(id)) continue;

        set.decided = id;
        this.accepted.add(id);
        this.dag.setStatus(id, 'accepted');
        newlyAccepted.push(id);
        progressed = true;

        for (const other of set.members) {
          if (other === id) continue;
          this.rejected.add(other);
          this.dag.setStatus(other, 'rejected');
          newlyRejected.push(other);
        }
      }
      if (!progressed) break;
    }

    return { accepted: newlyAccepted, rejected: newlyRejected };
  }

  private leaderOf(set: ConflictSetState): VertexId {
    let best = set.preference;
    let bestConf = this.vote(best).confidence;
    for (const m of set.members) {
      const c = this.vote(m).confidence;
      // Strictly greater: the incumbent keeps the seat on a tie.
      if (c > bestConf) {
        bestConf = c;
        best = m;
      }
    }
    return best;
  }

  private pastAccepted(id: VertexId, cap = 4096): boolean {
    const entry = this.dag.get(id);
    if (!entry) return false;
    const seen = new Set<VertexId>();
    const queue = [...entry.vertex.parents];
    let visited = 0;
    while (queue.length && visited < cap) {
      const cur = queue.shift()!;
      if (seen.has(cur)) continue;
      seen.add(cur);
      visited++;
      if (this.dag.isAnchor(cur)) continue;
      if (!this.accepted.has(cur)) return false;
    }
    return true;
  }

  /** A chit on a vertex is support for everything that vertex confirms. */
  private propagateConfidence(id: VertexId, cap = 4096): void {
    this.vote(id).confidence++;
    const entry = this.dag.get(id);
    if (!entry) return;
    const seen = new Set<VertexId>([id]);
    const queue = [...entry.vertex.parents];
    let visited = 0;
    while (queue.length && visited < cap) {
      const cur = queue.shift()!;
      if (seen.has(cur)) continue;
      seen.add(cur);
      visited++;
      if (this.dag.isAnchor(cur)) continue;
      this.vote(cur).confidence++;
      const e = this.dag.get(cur);
      if (!e) continue;
      for (const p of e.vertex.parents) if (!seen.has(p)) queue.push(p);
    }
  }

  /** Undecided vertices, most-recently-seen first - the polling worklist. */
  pending(limit = 64): VertexId[] {
    const out: { id: VertexId; seenAt: number }[] = [];
    for (const e of this.dag.entriesList()) {
      if (this.isDecided(e.vertex.id)) continue;
      out.push({ id: e.vertex.id, seenAt: e.seenAt });
    }
    out.sort((a, b) => b.seenAt - a.seenAt);
    return out.slice(0, limit).map((x) => x.id);
  }

  /**
   * How much work an attacker would have to undo to reverse this vertex,
   * expressed as accumulated confirmation weight. Reported in the explorer
   * so "security increases with every transaction" is a number, not a
   * marketing claim.
   */
  securityScore(id: VertexId): { confidence: number; weight: number; ratio: number; finalized: boolean } {
    const rec = this.vote(id);
    const entry = this.dag.get(id);
    return {
      confidence: rec.confidence,
      weight: entry?.weight ?? 0,
      ratio: this.dag.confirmationRatio(id),
      finalized: this.isAccepted(id),
    };
  }

  stats(): { accepted: number; rejected: number; pending: number; conflictSets: number; contested: number } {
    let contested = 0;
    for (const s of this.sets.values()) if (s.members.size > 1) contested++;
    return {
      accepted: this.accepted.size,
      rejected: this.rejected.size,
      pending: this.dag.size - this.accepted.size - this.rejected.size,
      conflictSets: this.sets.size,
      contested,
    };
  }
}
